import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useId,
  useState,
  useSyncExternalStore,
} from "react";
import type { FormContextType } from "./MagicForm";

/**
 * A member form registered in a {@link MagicFormGroup}. Forms register
 * themselves (see `useRegisterMagicFormInGroup`); group-level save/reset
 * buttons act on all dirty members at once, so a user can edit several
 * atoms without noticing they are separate forms.
 */
export interface MagicFormGroupMember {
  id: string;
  /** Save order, ascending; ties resolve by registration order. */
  order: number;
  /** Optional human-readable name used in error reporting. */
  label?: string;
  isDirty: boolean;
  loading: boolean;
  submit: (ignoreSuggestedChanges?: boolean) => Promise<void>;
  reset: () => Promise<void>;
}

export interface MagicFormGroupState {
  /** True when any member form has unsaved changes. */
  isDirty: boolean;
  dirtyCount: number;
  /** True while `saveAll` is running. */
  isSaving: boolean;
  /** True when any member form is still loading its atom. */
  isLoading: boolean;
}

export interface MagicFormGroupContextType {
  /** Upsert a member (forms re-register on every render to stay fresh). */
  register: (member: MagicFormGroupMember) => void;
  unregister: (id: string) => void;
  subscribe: (listener: () => void) => () => void;
  getState: () => MagicFormGroupState;
  /**
   * Save all dirty members sequentially in `(order, registration)` order.
   * Aborts on the first failure and rethrows: already-saved members stay
   * saved, the failed and remaining members keep their drafts. A
   * `MagicFormRevisionConflictError` is rethrown as-is so the failing
   * form's conflict modal takes over.
   */
  saveAll: () => Promise<void>;
  /** Reset all dirty members sequentially. */
  resetAll: () => Promise<void>;
}

export const MagicFormGroupContext = createContext<
  MagicFormGroupContextType | undefined
>(undefined);

type InternalMember = MagicFormGroupMember & { registrationIndex: number };

/** Headless store behind {@link MagicFormGroup}; exported for reuse/testing. */
export function createMagicFormGroupStore(): MagicFormGroupContextType {
  const members = new Map<string, InternalMember>();
  const listeners = new Set<() => void>();
  let registrationCounter = 0;
  let isSaving = false;
  let state: MagicFormGroupState = {
    isDirty: false,
    dirtyCount: 0,
    isSaving: false,
    isLoading: false,
  };

  const recompute = () => {
    let dirtyCount = 0;
    let isLoading = false;
    for (const member of members.values()) {
      if (member.isDirty) dirtyCount++;
      if (member.loading) isLoading = true;
    }
    const next: MagicFormGroupState = {
      isDirty: dirtyCount > 0,
      dirtyCount,
      isSaving,
      isLoading,
    };
    if (
      next.isDirty === state.isDirty &&
      next.dirtyCount === state.dirtyCount &&
      next.isSaving === state.isSaving &&
      next.isLoading === state.isLoading
    ) {
      return;
    }
    state = next;
    for (const listener of listeners) listener();
  };

  const sortedMembers = () =>
    [...members.values()].sort(
      (a, b) => a.order - b.order || a.registrationIndex - b.registrationIndex,
    );

  const runSequentially = async (
    action: (member: InternalMember) => Promise<void>,
  ) => {
    if (isSaving) return;
    isSaving = true;
    recompute();
    try {
      for (const snapshot of sortedMembers()) {
        // Re-read the live entry: earlier saves may have re-rendered later
        // forms and re-registered them with fresh state/closures. A member
        // that unregistered mid-run (its form unmounted) is gone from the map
        // and skipped — never called through a stale snapshot closure.
        const member = members.get(snapshot.id);
        if (!member?.isDirty) continue;
        await action(member);
      }
    } finally {
      isSaving = false;
      recompute();
    }
  };

  return {
    register: (member) => {
      const existing = members.get(member.id);
      members.set(member.id, {
        ...member,
        registrationIndex: existing?.registrationIndex ?? registrationCounter++,
      });
      recompute();
    },
    unregister: (id) => {
      members.delete(id);
      recompute();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getState: () => state,
    saveAll: () => runSequentially((member) => member.submit()),
    resetAll: () => runSequentially((member) => member.reset()),
  };
}

/**
 * Provider that lets multiple MagicForms (bound to different atoms) share a
 * single pair of save/reset buttons. Forms rendered inside register
 * automatically; a form outside any group behaves exactly as before.
 */
export function MagicFormGroup({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [store] = useState(createMagicFormGroupStore);
  return (
    <MagicFormGroupContext.Provider value={store}>
      {children}
    </MagicFormGroupContext.Provider>
  );
}

/** The surrounding group, or undefined when not rendered inside one. */
export function useMagicFormGroup(): MagicFormGroupContextType | undefined {
  return useContext(MagicFormGroupContext);
}

const EMPTY_GROUP_STATE: MagicFormGroupState = {
  isDirty: false,
  dirtyCount: 0,
  isSaving: false,
  isLoading: false,
};

/** Live aggregate state of the surrounding group (empty state without one). */
export function useMagicFormGroupState(): MagicFormGroupState {
  const group = useContext(MagicFormGroupContext);
  return useSyncExternalStore(
    group?.subscribe ?? (() => () => {}),
    group?.getState ?? (() => EMPTY_GROUP_STATE),
    group?.getState ?? (() => EMPTY_GROUP_STATE),
  );
}

export interface RegisterMagicFormInGroupOptions {
  context: Pick<
    FormContextType,
    "draft" | "isDirty" | "loading" | "submit" | "resetDraft"
  >;
  groupOrder?: number;
  excludeFromGroup?: boolean;
  groupLabel?: string;
}

/**
 * Registers a MagicForm in the surrounding {@link MagicFormGroup} (no-op
 * without one). Upserts on every render so the group always sees the
 * current dirty state and fresh submit/reset closures.
 */
export function useRegisterMagicFormInGroup({
  context,
  groupOrder = 0,
  excludeFromGroup = false,
  groupLabel,
}: RegisterMagicFormInGroupOptions): void {
  const group = useContext(MagicFormGroupContext);
  const id = useId();

  // Intentionally no dependency array: upsert on every render so the group
  // always sees fresh dirty state and submit/reset closures.
  useEffect(() => {
    if (!group) return;
    if (excludeFromGroup) {
      group.unregister(id);
      return;
    }
    group.register({
      id,
      order: groupOrder,
      label: groupLabel,
      isDirty: context.isDirty,
      loading: context.loading,
      submit: context.submit,
      reset: context.resetDraft,
    });
  });

  useEffect(() => {
    if (!group) return;
    return () => group.unregister(id);
  }, [group, id]);
}
